import { z } from 'zod';
import { TFunction } from 'i18next';

import type { OptionalFieldConfig } from '@/entities/layout';
import { isOptionalFieldKey, validateOptionalField } from '@/features/optional-fields';

// valueAsNumber turns a cleared number input into NaN; an optional number has to read that as "no value".
const nanToNull = (value: unknown) => (Number.isNaN(value) ? null : value);

/**
 * Mirrors the server-side rules of `ConsignmentLineService.validateWritePayload` and
 * `ConsignmentLineOptionalFieldsValidator` (doc/api/CONSIGNMENT_LINES_CRUD.md in the backend repo).
 * Cold chain needs a temperature range; Other needs one bound at least, as legacy ("below -15" has no lower one).
 */
export const getConsignmentLineDetailsFormSchema = (
  t: TFunction,
  isSubprojectRequired = false,
  optionalFields: OptionalFieldConfig[] = []
) => {
  const required = t('validity.valueMissing.defaultMessage');
  const maxLength = (labelKey: string, max: number) =>
    t('receive.confirm.error.maxLength', { field: t(labelKey), max });

  return z
    .object({
      productCategory: z.string().min(1, required),
      inventoryQuantity: z.number().int().min(1, t('validation.minValue', 'Must be greater than 0')),
      productLtuId: z.string().min(1, required),
      description: z.string().trim().min(1, required).max(255, maxLength('requestline.ownerItemDesc.label', 255)),

      handlingUnits: z.number().int().min(1, t('validation.minValue', 'Must be greater than 0')),
      handlingUnitTypeId: z.string().min(1, required),

      totalWeight: z.number().min(0.01, t('validation.minValue', 'Must be greater than 0')),
      totalVolume: z.number().min(0.01, t('validation.minValue', 'Must be greater than 0')),
      totalValue: z.preprocess(nanToNull, z.number().min(0).nullish()),

      ownerReference: z.string().max(40, maxLength('requestline.ownerItemIdentifier.label', 40)).optional(),
      expiryDate: z.string().optional(),
      batchNumber: z.string().optional(),
      productCode: z.string().optional(),
      subProjectCode: isSubprojectRequired ? z.string().min(1, required) : z.string().optional(),

      optionalField1: z.string().nullable().optional(),
      optionalField2: z.string().nullable().optional(),
      optionalField3: z.string().nullable().optional(),
      optionalField4: z.string().nullable().optional(),
      optionalField5: z.string().nullable().optional(),
      optionalField6: z.string().nullable().optional(),

      fragile: z.boolean(),
      dangerous: z.boolean(),
      regulated: z.boolean(),
      coldChain: z.boolean(),
      coldChainProfile: z.string(),
      coldMinTemp: z.preprocess(nanToNull, z.number().nullish()),
      coldMaxTemp: z.preprocess(nanToNull, z.number().nullish()),
      dangerousGoodsUnNumber: z.string().optional(),
      handlingInstructions: z.string().max(255, maxLength('requestline.handlingInstructions.label', 255)).optional(),
      showOnPackingList: z.boolean().optional()
    })
    .superRefine((values, ctx) => {
      if (values.coldChain && !values.coldChainProfile) {
        ctx.addIssue({ code: 'custom', path: ['coldChainProfile'], message: required });
      }
      if (
        values.coldChain &&
        values.coldChainProfile === 'OTHER' &&
        values.coldMinTemp == null &&
        values.coldMaxTemp == null
      ) {
        ctx.addIssue({ code: 'custom', path: ['coldMinTemp'], message: required });
        ctx.addIssue({ code: 'custom', path: ['coldMaxTemp'], message: required });
      }
      if (values.dangerous && !values.dangerousGoodsUnNumber) {
        ctx.addIssue({ code: 'custom', path: ['dangerousGoodsUnNumber'], message: required });
      }
      optionalFields.forEach((fieldConfig) => {
        const [[fieldKey, definition]] = Object.entries(fieldConfig);
        if (!isOptionalFieldKey(fieldKey)) return;
        const message = validateOptionalField(values[fieldKey] ?? null, definition, fieldKey);
        if (message) ctx.addIssue({ code: 'custom', path: [fieldKey], message });
      });
    });
};
